import React, { useState, useEffect } from 'react';
import { rulebookService } from '../services/rulebookService';
import '../styles/RulebookPage.css';

function Rulebook() {
  const [rulebook, setRulebook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tocOpen, setTocOpen] = useState(true);
  const [contentTocOpen, setContentTocOpen] = useState(true);

  useEffect(() => {
    loadRulebook();
  }, []);

  useEffect(() => {
    const handleContentTocToggle = (e) => {
      if (e.target.tagName === 'H2') {
        const toc = e.target.closest('.rulebook-toc');
        if (toc) {
          setContentTocOpen(prev => !prev);
        }
      }
    };

    document.addEventListener('click', handleContentTocToggle);
    return () => document.removeEventListener('click', handleContentTocToggle);
  }, []);

  const loadRulebook = async () => {
    try {
      const data = await rulebookService.getRulebook();
      if (data) {
        if (data.blocks && typeof data.blocks === 'string') {
          try {
            data.blocks = JSON.parse(data.blocks);
          } catch (e) {
            console.warn('Could not parse blocks', e);
          }
        }
        setRulebook(data);
      } else {
        setError('Rulebook not available');
      }
    } catch (err) {
      setError('Failed to load rulebook');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="rulebook-page">
        <div className="loading">Loading rulebook...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rulebook-page">
        <div className="error-message">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!rulebook) {
    return (
      <div className="rulebook-page">
        <div className="empty-state">
          <p>Rulebook not yet published. Please check back later.</p>
        </div>
      </div>
    );
  }

  const tableOfContents = rulebook.blocks
    ? rulebook.blocks.filter(b => ['section', 'subsection'].includes(b.type) && b.title)
    : [];

  const scrollToSection = (id) => {
    const element = document.getElementById(`section-${id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="rulebook-page">
      <div className="rulebook-container">
        {rulebook.edition && (
          <div className="rulebook-header-section">
            <h1 className="rulebook-title">{rulebook.edition}</h1>
            <p className="last-updated">
              Last Updated: {new Date(rulebook.last_updated_at).toLocaleDateString()}
            </p>
            {rulebook.admin_users && rulebook.admin_users.email && (
              <p className="updated-by">
                Updated by: {rulebook.admin_users.email}
              </p>
            )}
          </div>
        )}

        <div className="rulebook-layout">
          {tableOfContents.length > 0 && (
            <aside className="rulebook-sidebar">
              <nav className="rulebook-toc">
                <div className="toc-header">
                  <h3>Contents</h3>
                  <button
                    onClick={() => setTocOpen(!tocOpen)}
                    className="toc-toggle"
                    aria-label={tocOpen ? 'Collapse contents' : 'Expand contents'}
                  >
                    {tocOpen ? '−' : '+'}
                  </button>
                </div>
                {tocOpen && (
                  <ul>
                    {tableOfContents.map(section => (
                      <li key={section.id} className={`toc-${section.type}`}>
                        <button
                          onClick={() => scrollToSection(section.id)}
                          className="toc-link"
                        >
                          {section.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </nav>
            </aside>
          )}

          <main className="rulebook-content">
            <div
              dangerouslySetInnerHTML={{ __html: rulebook.content }}
              className={`content-body ${contentTocOpen ? 'toc-expanded' : 'toc-collapsed'}`}
            />
          </main>
        </div>
      </div>
    </div>
  );
}

export default Rulebook;
